import { motion } from "framer-motion";
import { ArrowUpRight, Recycle, Clock, Tag } from "lucide-react";

const IMGS = {
  furniture: "https://images.pexels.com/photos/9714795/pexels-photo-9714795.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  house: "https://images.unsplash.com/photo-1670175035057-eec020c03eb9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA6OTV8MHwxfHNlYXJjaHwzfHxqdW5rJTIwcmVtb3ZhbCUyMGhvdXNlJTIwY2xlYXJhbmNlJTIwZnVybml0dXJlJTIwd2FzdGUlMjByZWN5Y2xpbmd8ZW58MHx8fHwxNzkyNDM0MDI3fDA&ixlib=rb-4.1.0&q=85",
  garden: "https://images.pexels.com/photos/17577525/pexels-photo-17577525.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  recycle: "https://images.pexels.com/photos/37367055/pexels-photo-37367055.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const reveal = {
  hidden: { opacity: 0, y: 32 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] } }),
};

const ImgCard = ({ i, img, title, desc, span, testid }) => (
  <motion.div
    variants={reveal}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-60px" }}
    custom={i}
    className={`group relative col-span-12 overflow-hidden border border-white/10 bg-coal transition-colors duration-300 hover:border-brand/60 ${span}`}
    data-testid={testid}
  >
    <div className="relative h-56 overflow-hidden sm:h-64">
      <img src={img} alt={title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-coal via-coal/20 to-transparent" />
    </div>
    <div className="relative p-6">
      <div className="flex items-start justify-between gap-4">
        <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-paper">{title}</h3>
        <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 text-brand transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
      </div>
      <p className="mt-2 text-sm text-muted2">{desc}</p>
    </div>
  </motion.div>
);

const StatCard = ({ i, icon: Icon, value, label, testid }) => (
  <motion.div
    variants={reveal}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-60px" }}
    custom={i}
    className="col-span-12 flex flex-col justify-between border border-white/10 bg-coal p-6 transition-colors duration-300 hover:border-safety/60 sm:col-span-6 lg:col-span-4"
    data-testid={testid}
  >
    <Icon className="h-7 w-7 text-safety" />
    <div className="mt-8">
      <div className="font-display text-6xl font-black leading-none text-paper">{value}</div>
      <div className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-muted2">{label}</div>
    </div>
  </motion.div>
);

export default function Services() {
  return (
    <section id="diensten" className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28" data-testid="services-section">
      <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={0}>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand">// Wat wij ophalen</p>
        <h2 className="mt-3 font-display text-4xl font-black uppercase tracking-tight text-paper sm:text-5xl lg:text-6xl">
          Alles wat weg moet, <span className="text-outline">halen wij weg</span>
        </h2>
      </motion.div>
      <div className="mt-12 grid grid-cols-12 gap-4">
        <ImgCard i={1} img={IMGS.furniture} title="Meubels afvoeren" desc="Zetels, kasten, bedden, tafels — van één stuk tot een volledige inboedel. Wij dragen, laden en voeren af." span="lg:col-span-8" testid="service-card-meubels" />
        <StatCard i={2} icon={Recycle} value="95%" label="Hergebruik & recycling" testid="stat-card-recycling" />
        <ImgCard i={3} img={IMGS.house} title="Woningontruiming" desc="Volledige huisopkuis na verhuis, overlijden of verkoop. Bezemschoon opgeleverd, zonder zorgen." span="sm:col-span-6 lg:col-span-4" testid="service-card-woning" />
        <ImgCard i={4} img={IMGS.garden} title="Tuinafval" desc="Snoeiafval, oude tuinmeubelen, schuttingen en grond. Je tuin weer helemaal netjes." span="sm:col-span-6 lg:col-span-4" testid="service-card-tuin" />
        <ImgCard i={5} img={IMGS.recycle} title="Recyclage" desc="Metaal, elektronica en materialen krijgen een tweede leven. Goed voor je portemonnee én de planeet." span="sm:col-span-6 lg:col-span-4" testid="service-card-recyclage" />
        <StatCard i={6} icon={Clock} value="24u" label="Ophaling binnen 24u mogelijk" testid="stat-card-snel" />
        <StatCard i={7} icon={Tag} value="€0" label="Offerte is altijd gratis" testid="stat-card-prijs" />
      </div>
    </section>
  );
}
